import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
  Text,
  Keyboard,

} from "react-native";
import { useThemeSettings } from "../../../ThemeProvider";
import { CustomIcon } from "../../../components/CustomIcon";
import { TextInput } from "react-native-paper";
import { useTranslation } from "react-i18next";
import { useVoice } from "../../../hooks/useVoice";
import { GradientStrokeBox } from "../../../components/GradientStrokeBox";
import CustomLinearGradient, {
  GRADIENT_TYPE,
} from "../../../components/CustomLinearGradient";
import { TrackedTouchable } from "../../../analytics/components";
import getGlobalStyles from "../../../globalStyles";
import { APP_CONFIG_KEY, getConfig } from "../../../services/apiService";

const ChatInputBar: React.FC<{
  onSend?: (text: string, label?: string) => void;
  showSuggestions?: boolean;
  onFocusChange?: (focused: boolean) => void;
}> = ({ onSend, showSuggestions, onFocusChange }) => {
  const { theme } = useThemeSettings();

  const { t } = useTranslation();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const { transcript, listening, start, stop, error, isTranscribing } =
    useVoice();
   const [dots, setDots] = useState("");
    useEffect(() => {
    if (!listening) {
      setDots("");
      return;
    }

    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return "";
        return prev + ".";
      });
    }, 400);

    return () => clearInterval(interval);
  }, [listening]);

  React.useEffect(() => {
    async function fetchSuggestions() {
      const s = showSuggestions ? await getConfig(APP_CONFIG_KEY.ASSISTANT_SUGGESTIONS) : [];
      setSuggestions(s || []);
    }
    fetchSuggestions();
  }, [showSuggestions]);
  React.useEffect(() => {
    if (transcript && transcript.trim().length > 0) {
      setValue(transcript);
    }
  }, [transcript]);

  const handleSubmit = () => {
    if (!value.trim()) return;
    onSend?.(value.trim());
    setValue("");
    Keyboard.dismiss();
    if (listening) {
      stop();
    }
  };

  const onButtonClick = () => {
    if (value?.trim()) {
      handleSubmit();
    }
  };

  return (
    <View style={[gStyles.centerColumn]}>
      {showSuggestions && (
        <ScrollView
          horizontal
          style={{ minHeight: 32 }}
          showsHorizontalScrollIndicator={false}
        >
          <View style={[gStyles.centerRow, { gap: 15 }]}>
            {suggestions?.map((item) => (
              <TrackedTouchable
                key={item.key}
                style={{ flex: 1, minWidth: item.width, height: 30 }}
                onPress={() => {
                  onSend?.(item.value, item?.chat_text);
                  Keyboard.dismiss();
                }}
              >
                <GradientStrokeBox
                  stroke={2}
                  wrapperStyle={{
                    borderRadius: 10,
                    flex: 1,
                    minWidth: item.width,
                    height: 30,
                  }}
                  style={{}}
                >
                  <CustomLinearGradient
                    gradientType={GRADIENT_TYPE.BASE}
                    style={{
                      margin: 2,
                      borderRadius: 10,
                      flexDirection: "row",
                    }}
                  >
                    <Text
                      style={{
                        marginHorizontal: 10,
                        alignSelf: "center",
                        color: theme.colors.primary,
                      }}
                    >
                      {item.label}
                    </Text>
                  </CustomLinearGradient>
                </GradientStrokeBox>
              </TrackedTouchable>
            ))}
          </View>
        </ScrollView>
      )}
      <View style={styles.wrapper}>
        <GradientStrokeBox
          stroke={2}
          wrapperStyle={{
            flex: 1,
            minHeight: 56,
            borderRadius: 30,
          }}
        >
         <View style={{ flex: 1, position: "relative" }}>
         <TextInput
              mode="outlined"
              value={value}
              onFocus={() => onFocusChange?.(true)}
              onBlur={() => onFocusChange?.(false)}
              onChangeText={setValue}
              placeholder={!listening ? t("screens.assistant.chatInputPlaceholder") : ""}
              placeholderTextColor={theme?.colors?.primary}
              textColor={theme?.colors?.primary}
              onSubmitEditing={handleSubmit}
              returnKeyType="send"
              style={{
                flex: 1,
                height: 56,
                backgroundColor: "transparent",
              }}
              contentStyle={{
                height: 56,
              }}
              outlineStyle={{
                borderWidth: 0,
              }}
            />

            {listening && !value && (
              <Text
                style={{
                  position: "absolute",
                  left: 16,
                  top: 18,
                  fontStyle: "italic",
                  color: theme?.colors?.primary,
                }}
              >
                Listening{dots}
              </Text>
            )}
      </View>
        </GradientStrokeBox>

        <GradientStrokeBox
          stroke={2}
          style={
            Platform.OS !== "web" && { width: 56, height: 50, paddingLeft: 4 }
          }
        >
          {value.length === 0 ? (
            <TrackedTouchable
              onPress={() => {
                if (listening) {
                  stop();
                } else {
                  start();
                }
              }}
              style={styles.micBtn}
              disabled={isTranscribing}
            >
              <CustomIcon
                icon={
                  isTranscribing
                    ? "loading"
                    : listening
                      ? "pause"
                      : "microphone"
                }
                size={24}
                color={theme?.colors?.primary}
              />
            </TrackedTouchable>
          ) : (
            <TrackedTouchable
              onPress={onButtonClick}
              style={[styles.micBtn]}
            >
              <CustomIcon
                icon={"submit"}
                size={24}
                color={theme?.colors?.primary}
              />
            </TrackedTouchable>
          )}
        </GradientStrokeBox>
      </View>
    </View>
  );
};

export default ChatInputBar;

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 6,
    gap: 12,
  },

  textInput: {
    fontSize: 15,
    height: 44,
  },

  micBtn: {
    justifyContent: "center",
    alignItems: "center",
    padding: 12
  },
});